import React, { useState, useEffect } from 'react';
import { Tabs, Empty, message } from 'antd';
import {
  FileTextOutlined,
  CodeOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotTemplate } from '../../../types/krybot';
import { TemplateDetailsTab } from './TemplateDetailsTab';
import { TemplateMetadataTab } from './TemplateMetadataTab';
import { TemplatePromptBuilderTab } from './TemplatePromptBuilderTab';
import styles from './TemplateManagement.module.css';

type TemplateTabKey = 'details' | 'metadata' | 'prompt-builder';

interface TemplateDetailsPanelProps {
  templateId: string | null;
  isCreating: boolean;
  onSave: () => void;
  onCancel: () => void;
  onTemplateChange: (template: KrybotTemplate | null) => void;
}

export const TemplateDetailsPanel: React.FC<TemplateDetailsPanelProps> = ({
  templateId,
  isCreating,
  onSave,
  onCancel,
  onTemplateChange,
}) => {
  const [template, setTemplate] = useState<KrybotTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TemplateTabKey>('details');
  const [templateData, setTemplateData] = useState<Partial<KrybotTemplate>>({});

  useEffect(() => {
    if (templateId && !isCreating) {
      loadTemplate();
    } else if (isCreating) {
      setTemplate(null);
      setTemplateData({});
    } else {
      setTemplate(null);
      setTemplateData({});
    }
  }, [templateId, isCreating]);

  const loadTemplate = async () => {
    if (!templateId) return;
    try {
      setLoading(true);
      // First get the template from the list to get the template_key
      const templates = await KrybotService.listTemplates();
      const foundTemplate = templates.find(t => t.id === templateId);
      if (!foundTemplate) {
        throw new Error('Template not found');
      }
      // Use template_key to get full template details
      const loadedTemplate = await KrybotService.getTemplate(foundTemplate.template_key);
      setTemplate(loadedTemplate);
      setTemplateData(loadedTemplate);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load template';
      message.error(`Failed to load template: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateDataChange = (updates: Partial<KrybotTemplate>) => {
    const updated = { ...templateData, ...updates };
    setTemplateData(updated);
    if (template) {
      const updatedTemplate = { ...template, ...updates };
      setTemplate(updatedTemplate);
      onTemplateChange(updatedTemplate);
    }
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        // Create new template
        const newTemplate = await KrybotService.createTemplate(templateData as any);
        message.success('Template created successfully');
        onTemplateChange(newTemplate);
        onSave();
      } else if (template?.template_key) {
        // Update existing template
        await KrybotService.updateTemplate(template.template_key, templateData as any);
        message.success('Template updated successfully');
        await loadTemplate(); // Reload to get latest data
        onSave();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save template';
      message.error(`Failed to save template: ${errorMsg}`);
    }
  };

  if (!templateId && !isCreating) {
    return (
      <div className="template-details-panel-empty">
        <Empty
          description="Select a template from the list to view details"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  const currentTemplate = template || (isCreating ? null : template);

  return (
    <div className="template-details-panel">
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TemplateTabKey)}
        items={[
          {
            key: 'details',
            label: (
              <span>
                <FileTextOutlined />
                Details
              </span>
            ),
            children: (
              <TemplateDetailsTab
                template={currentTemplate}
                templateData={templateData}
                isCreating={isCreating}
                onDataChange={handleTemplateDataChange}
                onSave={handleSave}
                onCancel={onCancel}
                loading={loading}
              />
            ),
          },
          {
            key: 'metadata',
            label: (
              <span>
                <CodeOutlined />
                Metadata
              </span>
            ),
            children: (
              <TemplateMetadataTab
                template={currentTemplate}
                templateData={templateData}
                isCreating={isCreating}
                onDataChange={handleTemplateDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
          {
            key: 'prompt-builder',
            label: (
              <span>
                <MessageOutlined />
                Prompt Builder
              </span>
            ),
            children: (
              <TemplatePromptBuilderTab
                template={currentTemplate}
                templateData={templateData}
                isCreating={isCreating}
                onDataChange={handleTemplateDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

